import type {
  SpecsEditorNav,
  SpecsEditorSession,
  SpecsLayer,
} from "../../../containers/SpecsEditor/types";
import { getElementSelectionKey } from "../../../containers/SpecsEditor/positionsLib";

export type PositionNavPayload =
  | string
  | {
      positionId?: string;
      partName?: string;
      elementName?: string;
      styleName?: string;
      layerId?: string;
    }
  | undefined;

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function touch(state: SpecsEditorSession) {
  state.updatedAt = new Date().toISOString();
}

function getNavProductName(state: SpecsEditorSession): string | undefined {
  return "product" in state.nav ? state.nav.product : undefined;
}

function getNavVersionName(state: SpecsEditorSession): string | undefined {
  return "version" in state.nav ? state.nav.version : undefined;
}

export function createEmptyNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "empty",
    product: getNavProductName(state),
    version: getNavVersionName(state),
  };
}

export function createSettingsNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "settings",
    product: getNavProductName(state),
    version: getNavVersionName(state),
  };
}

export function createImportNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "import",
    product: getNavProductName(state),
    version: getNavVersionName(state),
  };
}

export function createHistoryNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "history",
    product: getNavProductName(state),
    version: getNavVersionName(state),
  };
}

function resolveElementStyle(
  state: SpecsEditorSession,
  partName: string,
  elementName: string,
  styleName?: string
): string {
  if (styleName) {
    return styleName;
  }

  const scopedVisibleLayer = Object.values(state.layerRuntimeByKey).find(
    (layer) => {
      return (
        layer.visible &&
        layer.modelLayerPath?.partName === partName &&
        layer.modelLayerPath?.elementName === elementName &&
        !!layer.modelLayerPath?.styleName
      );
    }
  );
  const scopedVisibleBinding = scopedVisibleLayer?.modelLayerPath;
  if (scopedVisibleBinding?.styleName) {
    return scopedVisibleBinding.styleName;
  }

  const scopedLayer = Object.values(state.layerRuntimeByKey).find((layer) => {
    return (
      layer.modelLayerPath?.partName === partName &&
      layer.modelLayerPath?.elementName === elementName &&
      !!layer.modelLayerPath?.styleName
    );
  });
  return scopedLayer?.modelLayerPath?.styleName ?? "";
}

export function getScopedLayers(
  state: SpecsEditorSession,
  partName: string,
  elementName: string,
  styleName: string
): SpecsLayer[] {
  return Object.values(state.layerRuntimeByKey)
    .filter((layer) => {
      return (
        layer.modelLayerPath?.partName === partName &&
        layer.modelLayerPath?.elementName === elementName &&
        layer.modelLayerPath?.styleName === styleName
      );
    })
    .map((layer) => ({
      id: layer.key,
      name: layer.modelLayerPath.layerName ?? layer.key,
      visible: layer.visible,
      baseColor: layer.baseColor,
      zIndex: layer.zIndex,
      modelLayerPath: layer.modelLayerPath,
      highlight: layer.highlight,
      mask: layer.mask,
    }));
}

export function getScopedVisibleLayers(
  state: SpecsEditorSession,
  partName: string,
  elementName: string,
  styleName: string
): SpecsLayer[] {
  return getScopedLayers(state, partName, elementName, styleName).filter(
    (layer) => layer.visible
  );
}

export function createElementNav(
  state: SpecsEditorSession,
  partName: string,
  elementName: string,
  styleName?: string
): SpecsEditorNav {
  return {
    kind: "element",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
    part: partName,
    element: elementName,
    style: resolveElementStyle(state, partName, elementName, styleName),
  };
}

export function createLayerNavFromLayer(
  state: SpecsEditorSession,
  layer: Pick<SpecsLayer, "id" | "modelLayerPath">
): SpecsEditorNav {
  return {
    kind: "layer",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
    part: layer.modelLayerPath?.partName ?? "",
    element: layer.modelLayerPath?.elementName ?? "",
    style: layer.modelLayerPath?.styleName ?? "",
    layerId: layer.id,
  };
}

export function createScopedNav(
  state: SpecsEditorSession,
  partName: string,
  elementName: string,
  styleName?: string
): SpecsEditorNav {
  const resolvedStyle = resolveElementStyle(
    state,
    partName,
    elementName,
    styleName
  );
  const firstVisibleLayer = getScopedVisibleLayers(
    state,
    partName,
    elementName,
    resolvedStyle
  )[0];
  if (firstVisibleLayer) {
    return createLayerNavFromLayer(state, firstVisibleLayer);
  }
  const firstScopedLayer = getScopedLayers(
    state,
    partName,
    elementName,
    resolvedStyle
  )[0];
  if (firstScopedLayer) {
    return createLayerNavFromLayer(state, firstScopedLayer);
  }
  return createElementNav(state, partName, elementName, resolvedStyle);
}

export function createFirstAvailableLayerNav(
  state: SpecsEditorSession
): SpecsEditorNav | null {
  const runtimeLayers = Object.values(state.layerRuntimeByKey);
  const layer = runtimeLayers.find((item) => item.visible) ?? runtimeLayers[0];
  return layer ? createLayerNavFromLayer(state, layer) : null;
}

export function createPositionsNav(
  state: SpecsEditorSession,
  payload?: PositionNavPayload
): SpecsEditorNav {
  const requested =
    typeof payload === "string" ? { positionId: payload } : (payload ?? {});
  const currentScope =
    state.nav.kind === "layer" ||
    state.nav.kind === "element" ||
    state.nav.kind === "positions"
      ? {
          partName: state.nav.part,
          elementName: state.nav.element,
          styleName: state.nav.style,
          layerId:
            state.nav.kind === "layer" || state.nav.kind === "positions"
              ? state.nav.layerId
              : undefined,
        }
      : null;
  const fallbackScopedLayer =
    Object.values(state.layerRuntimeByKey).find((layer) => {
      return (
        !!layer.modelLayerPath?.partName &&
        !!layer.modelLayerPath?.elementName &&
        !!layer.modelLayerPath?.styleName &&
        layer.visible
      );
    }) ??
    Object.values(state.layerRuntimeByKey).find((layer) => {
      return (
        !!layer.modelLayerPath?.partName &&
        !!layer.modelLayerPath?.elementName &&
        !!layer.modelLayerPath?.styleName
      );
    });
  const fallbackScopedBinding = fallbackScopedLayer?.modelLayerPath;
  const partName =
    requested.partName ??
    currentScope?.partName ??
    fallbackScopedBinding?.partName ??
    undefined;
  const elementName =
    requested.elementName ??
    currentScope?.elementName ??
    fallbackScopedBinding?.elementName;
  const styleName =
    partName && elementName
      ? resolveElementStyle(
          state,
          partName,
          elementName,
          requested.styleName ?? currentScope?.styleName
        )
      : undefined;
  const scopedLayer =
    partName && elementName && styleName
      ? (getScopedVisibleLayers(state, partName, elementName, styleName)[0] ??
        getScopedLayers(state, partName, elementName, styleName)[0])
      : null;
  return {
    kind: "positions",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
    positionId: requested.positionId,
    part: partName,
    element: elementName,
    style: styleName,
    layerId: requested.layerId ?? scopedLayer?.id ?? currentScope?.layerId,
  };
}

export function createMetadataNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "metadata",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
  };
}

export function createPartsNav(
  state: SpecsEditorSession,
  partName?: string
): SpecsEditorNav {
  return {
    kind: "parts",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
    part: partName,
  };
}

export function createElementsNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "elements",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
  };
}

export function createStylesNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "styles",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
  };
}

export function createLayerSpecsNav(state: SpecsEditorSession): SpecsEditorNav {
  const currentScope =
    state.nav.kind === "layer" ||
    state.nav.kind === "element" ||
    state.nav.kind === "positions" ||
    state.nav.kind === "layerSpecs"
      ? state.nav
      : null;
  const firstLayer =
    Object.values(state.layerRuntimeByKey).find((layer) => layer.visible) ??
    Object.values(state.layerRuntimeByKey)[0] ??
    null;
  const fallbackPath = firstLayer?.modelLayerPath ?? null;

  return {
    kind: "layerSpecs",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
    part: currentScope?.part || fallbackPath?.partName,
    element: currentScope?.element || fallbackPath?.elementName,
    style: currentScope?.style || fallbackPath?.styleName,
    layerName:
      currentScope && "layerName" in currentScope
        ? currentScope.layerName
        : fallbackPath?.layerName,
  };
}

export function createShippingNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "shipping",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
  };
}

export function createGlobalNav(state: SpecsEditorSession): SpecsEditorNav {
  return {
    kind: "global",
    product: getNavProductName(state) ?? "",
    version: getNavVersionName(state) ?? "",
  };
}

export function createFamiliesNav(
  state: SpecsEditorSession,
  specName?: string
): SpecsEditorNav {
  return {
    kind: "families",
    product: getNavProductName(state),
    version: getNavVersionName(state),
    specName,
  };
}

export function createFabricsNav(
  state: SpecsEditorSession,
  specName?: string
): SpecsEditorNav {
  return {
    kind: "fabrics",
    product: getNavProductName(state),
    version: getNavVersionName(state),
    specName,
  };
}

export function createDecorationsNav(
  state: SpecsEditorSession,
  specName?: string
): SpecsEditorNav {
  return {
    kind: "decorations",
    product: getNavProductName(state),
    version: getNavVersionName(state),
    specName,
  };
}

export function createFamiliesHistoryNav(
  state: SpecsEditorSession,
  specName?: string
): SpecsEditorNav {
  return {
    kind: "familiesHistory",
    product: getNavProductName(state),
    version: getNavVersionName(state),
    specName,
  };
}

export function createFabricsHistoryNav(
  state: SpecsEditorSession,
  specName?: string
): SpecsEditorNav {
  return {
    kind: "fabricsHistory",
    product: getNavProductName(state),
    version: getNavVersionName(state),
    specName,
  };
}

export function createDecorationsHistoryNav(
  state: SpecsEditorSession,
  specName?: string
): SpecsEditorNav {
  return {
    kind: "decorationsHistory",
    product: getNavProductName(state),
    version: getNavVersionName(state),
    specName,
  };
}

export function createDesignNav(state: SpecsEditorSession): SpecsEditorNav {
  if (
    (state.nav.kind === "layer" ||
      state.nav.kind === "element" ||
      state.nav.kind === "positions") &&
    state.nav.part &&
    state.nav.element
  ) {
    return createScopedNav(
      state,
      state.nav.part,
      state.nav.element,
      state.nav.style
    );
  }

  return createFirstAvailableLayerNav(state) ?? createEmptyNav(state);
}

export function getPreferredNewLayerScope(
  state: SpecsEditorSession
): { partName: string; elementName: string; styleName: string } | null {
  if (
    state.nav.kind === "layer" ||
    state.nav.kind === "element" ||
    state.nav.kind === "positions"
  ) {
    const partName = state.nav.part ?? "";
    const elementName = state.nav.element ?? "";
    const styleName = resolveElementStyle(
      state,
      partName,
      elementName,
      state.nav.style
    );
    if (partName && elementName && styleName) {
      return { partName, elementName, styleName };
    }
  }

  const firstScopedLayer =
    Object.values(state.layerRuntimeByKey).find((layer) => {
      return (
        !!layer.modelLayerPath?.partName &&
        !!layer.modelLayerPath?.elementName &&
        !!layer.modelLayerPath?.styleName &&
        layer.visible
      );
    }) ??
    Object.values(state.layerRuntimeByKey).find((layer) => {
      return (
        !!layer.modelLayerPath?.partName &&
        !!layer.modelLayerPath?.elementName &&
        !!layer.modelLayerPath?.styleName
      );
    });
  if (!firstScopedLayer) return null;
  const firstScopedBinding = firstScopedLayer.modelLayerPath;
  if (!firstScopedBinding) return null;

  const partName = firstScopedBinding.partName || "";
  const elementName = firstScopedBinding.elementName || "";
  const styleName = resolveElementStyle(state, partName, elementName);

  if (!partName || !elementName || !styleName) return null;
  return { partName, elementName, styleName };
}
